import { useEffect, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";

import {
  getBottomCtaOccupied,
  isBottomCtaSourceOccupied,
  subscribeBottomCtaOccupied,
} from "@/components/care/transparentCare/bottomCtaOccupancy";
import evvyaiLogo from "@/components/chat/evvyai-logo-beta.svg";
import widgetBg from "@/components/chat/widget-bg.png";
import { useChat } from "@/contexts/chatContext";

const DEFAULT_BOTTOM_PX = 20;
const DEFAULT_RIGHT_PX = 20;

// Compute the bottom/right offsets the widget should sit at so it clears
// known page-level bottom CTAs:
//   - the standard results page's "View Your Plan" sticky footer. Fertility-V and UTI
//     results share the /results/ route but have no footer, so it reports itself.
//   - the program sticky add-to-cart, wherever it renders (Shop Care, CTP PDP, and
//     treatment plan PDPs), and the cart panel's own checkout button. Each reports its own
//     occupancy, so this stays correct without re-deriving their route, scroll, and
//     eligibility gating; offsets right-align with the bar.
const useWidgetOffsets = () => {
  const [isMobile, setIsMobile] = useState(false);
  const bottomCtaOccupied = useSyncExternalStore(
    subscribeBottomCtaOccupied,
    getBottomCtaOccupied
  );
  const resultsFooterShown = useSyncExternalStore(
    subscribeBottomCtaOccupied,
    () => isBottomCtaSourceOccupied("results-sticky-footer")
  );

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 639px)");
    setIsMobile(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  if (resultsFooterShown) {
    return { bottom: isMobile ? 120 : 96, right: DEFAULT_RIGHT_PX };
  }
  if (bottomCtaOccupied) {
    return { bottom: isMobile ? 100 : 144, right: isMobile ? 16 : 48 };
  }
  return { bottom: DEFAULT_BOTTOM_PX, right: DEFAULT_RIGHT_PX };
};

export default function ChatToggle() {
  const { pathname } = useLocation();
  const chat = useChat();
  const { bottom, right } = useWidgetOffsets();

  if (!chat || pathname.startsWith("/provider/order")) return null;

  const { toggle, isOpen } = chat;

  if (isOpen) return null;

  // z-40 keeps the widget under dialogs (modals render at z-50) but above page content.
  return (
    <button
      onClick={toggle}
      style={{
        bottom: `${bottom}px`,
        right: `${right}px`,
        // Slide smoothly when a bottom CTA appears/disappears; keep hover
        // and active scale snappy.
        transition:
          "bottom 300ms ease-out, right 300ms ease-out, transform 150ms ease-out",
      }}
      className="fixed z-40 hover:scale-105 active:scale-95"
      aria-label="Open chat"
    >
      <div
        className="relative flex items-center h-[40px] px-[12px] rounded-[12px] overflow-hidden"
        style={{
          background:
            "linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.9)) padding-box, linear-gradient(135deg, rgba(179,231,247,0.6) 0%, rgba(2,173,255,0.4) 30%, rgba(200,156,217,0.4) 70%, rgba(245,184,227,0.3) 100%) border-box",
          border: "1px solid transparent",
          boxShadow:
            "-2px -2px 4px 0 rgba(179, 231, 247, 0.4), 2px 2px 4px 0 rgba(245, 184, 227, 0.15)",
          backdropFilter: "blur(2px)",
        }}
      >
        <img
          src={widgetBg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40 pointer-events-none rounded-[12px]"
        />
        <img src={evvyaiLogo} alt="EvvyAI" className="h-[24px] relative z-10" />
      </div>
    </button>
  );
}
